"use client";

import { Facebook, Instagram, Linkedin, Youtube, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="grid grid-cols-2 gap-x-8 gap-y-3">
        {Array.from({
        length: 8
      }).map((_, i) => <div key={i} data-index={i} className="h-5 w-28 bg-[hsl(178_15%_40%/0.1)] animate-pulse rounded-md" />)}
      </div>;
  }
  const items = menuItems.slice(0, 8);
  const padded = [...items, ...Array.from({
    length: Math.max(0, 8 - items.length)
  })];
  return <ul className="grid grid-cols-2 gap-x-8 gap-y-3">
      {padded.map((item, i) => <li data-index={i} key={i} className="flex items-baseline gap-2">
          <span className="font-mono text-xs tracking-widest" style={{
        color: "hsl(174 75% 38%)"
      }} aria-hidden="true">
            {`→${String(i + 1).padStart(2, "0")}`}
          </span>
          {item ? <Link to={(item as {
        href?: string;
      }).href || "#"} className="group relative inline-block text-[15px] font-default font-medium text-[hsl(222_47%_15%)] hover:text-[hsl(174_75%_38%)] transition-colors duration-200">
              <span className="relative">
                {(item as {
            label: string;
          }).label}
                <svg className="absolute -bottom-1 left-0 w-full h-1 opacity-0 group-hover:opacity-100 transition-opacity" viewBox="0 0 80 6" fill="none" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M2 3 Q 15 0, 28 3 T 54 3 T 78 3" stroke="hsl(174 75% 38%)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </Link> : <span className="text-[15px] font-default font-medium text-[hsl(222_47%_15%/0.3)]">—</span>}
        </li>)}
    </ul>;
}
const socials = [{
  name: "Linkedin",
  href: "#",
  rotation: "-rotate-3"
}, {
  name: "Instagram",
  href: "#",
  rotation: "rotate-3"
}, {
  name: "Facebook",
  href: "#",
  rotation: "-rotate-2"
}, {
  name: "Youtube",
  href: "#",
  rotation: "rotate-2"
}];
function SocialIcon({
  name
}: {
  name: string;
}) {
  const cls = "w-5 h-5";
  if (name === "Linkedin") return <Linkedin className={cls} strokeWidth={2.5} />;
  if (name === "Instagram") return <Instagram className={cls} strokeWidth={2.5} />;
  if (name === "Facebook") return <Facebook className={cls} strokeWidth={2.5} />;
  return <Youtube className={cls} strokeWidth={2.5} />;
}
export default function Footer() {
  return <section data-section-id="1128" id="footer" className="relative overflow-hidden bg-[hsl(222_47%_11%)] py-20 md:py-24 dark">
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" aria-hidden="true" style={{
      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><g fill='none' stroke='hsl(174,75%25,38%25)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M20 25 l4 -8 l4 8 l8 0 l-6 6 l2 8 l-8 -4 l-8 4 l2 -8 l-6 -6 z'/><path d='M120 30 l3 6 l6 0 l-5 4 l2 6 l-6 -4 l-6 4 l2 -6 l-5 -4 l6 0 z'/><path d='M70 60 l-4 8 l6 0 l-3 8 l8 -10 l-6 0 l5 -8 z'/><path d='M130 90 q 10 -8 18 0 q -4 6 -10 6 q -6 0 -8 -6 z M134 90 l4 4 M140 88 l2 6'/><path d='M30 110 q 8 -6 14 0 q -3 4 -7 4 q -4 0 -7 -4 z M34 110 l3 3'/><path d='M90 125 l3 -6 l3 6 l6 0 l-5 4 l2 6 l-6 -4 l-6 4 l2 -6 l-5 -4 z'/><path d='M55 30 l8 4 l-8 4 M60 28 l0 12'/><path d='M105 130 q -4 -8 4 -10 q 8 -2 6 6 q 0 4 -5 4 q -3 0 -5 0 z'/></g></svg>")`,
      backgroundRepeat: "repeat"
    }} />

      <div className="pointer-events-none absolute top-4 left-4 w-3 h-3" aria-hidden="true">
        <div className="absolute top-0 left-0 w-3 h-[3px] bg-[hsl(174_75%_38%)]" />
        <div className="absolute top-0 left-0 w-[3px] h-3 bg-[hsl(174_75%_38%)]" />
      </div>
      <div className="pointer-events-none absolute top-4 right-4 w-3 h-3" aria-hidden="true">
        <div className="absolute top-0 right-0 w-3 h-[3px] bg-[hsl(174_75%_38%)]" />
        <div className="absolute top-0 right-0 w-[3px] h-3 bg-[hsl(174_75%_38%)]" />
      </div>
      <div className="pointer-events-none absolute bottom-20 left-4 w-3 h-3" aria-hidden="true">
        <div className="absolute bottom-0 left-0 w-3 h-[3px] bg-[hsl(174_75%_38%)]" />
        <div className="absolute bottom-0 left-0 w-[3px] h-3 bg-[hsl(174_75%_38%)]" />
      </div>
      <div className="pointer-events-none absolute bottom-20 right-4 w-3 h-3" aria-hidden="true">
        <div className="absolute bottom-0 right-0 w-3 h-[3px] bg-[hsl(174_75%_38%)]" />
        <div className="absolute bottom-0 right-0 w-[3px] h-3 bg-[hsl(174_75%_38%)]" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-4">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-10 w-auto" />
            </Link>
            <p className="mt-6 text-[17px] leading-snug font-default text-white">
              —Global Sourcing. Clinical Excellence.
            </p>

            <div className="mt-8 flex items-center gap-4">
              {socials.map((s, i) => <Link data-index={i} key={s.name} to={s.href} aria-label={s.name} className={`group relative inline-flex items-center justify-center w-11 h-11 rounded-lg border-[2.5px] border-[hsl(222_47%_11%)] bg-[hsl(174_75%_38%)] text-white ${s.rotation} hover:rotate-0 transition-transform duration-300`} style={{
              boxShadow: "3px 3px 0 hsl(0 0% 100%)"
            }}>
                  <SocialIcon name={s.name} />
                  <svg className="absolute -top-1 -right-1 w-3 h-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M2 2 Q 10 2, 10 10 L 2 2 Z" fill="hsl(0 0% 100%)" stroke="hsl(222 47% 11%)" strokeWidth="1" />
                  </svg>
                </Link>)}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.18em] mb-5 text-[hsl(174_75%_38%)]">
              § COMPANY
            </p>
            <MenuProvider menu_id="footer_menu">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div className="md:col-span-2">
            <Link to="#" className="font-mono text-xs uppercase tracking-[0.18em] mb-5 block text-[hsl(174_75%_38%)] hover:opacity-80 transition-opacity">
              § COMPLIANCE
            </Link>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">
              Certifications
            </p>
            <div className="flex flex-wrap gap-2">
              {["ISO 13485", "CE Mark", "FDA"].map((cert, i) => <span key={cert} data-index={i} className="px-2 py-1 rounded-md border border-[hsl(174_75%_38%/0.3)] bg-[hsl(174_75%_38%/0.1)] text-[10px] font-mono text-[hsl(174_75%_38%)] uppercase tracking-wider">
                  {cert}
                </span>)}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.18em] mb-4 text-[hsl(174_75%_38%)]">
              § PARTNERSHIP
            </p>
            <Button asChild className="bg-[hsl(174_75%_38%)] hover:bg-[hsl(174_75%_38%)] text-white border-[2.5px] border-[hsl(222_47%_11%)] rounded-lg font-default font-semibold text-base px-5 py-6 h-auto transition-transform duration-150 hover:translate-x-1 hover:translate-y-1" style={{
            boxShadow: "4px 4px 0 hsl(0 0% 100%)"
          }}>
              <Link to="/HomePage">
                Become a Partner
                <ArrowRight className="ml-1 w-4 h-4" strokeWidth={3} />
              </Link>
            </Button>

            <div className="mt-10">
              <p className="font-mono text-xs uppercase tracking-[0.18em] leading-relaxed text-[hsl(174_75%_38%)]">
                § GLOBAL HQ
                <br />
                <span className="text-white">· INNOVATION HUB</span>
              </p>
              <p className="mt-3 font-mono text-[11px] tracking-wider text-white/60">
                MS Biotechnology Co., Ltd.
                <br />
                Global Medical Distribution
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t-2 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-[hsl(174_75%_38%)]">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-white">
            © 2025 MS BIOTECHNOLOGY CO., LTD.
          </p>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-[hsl(174_75%_38%)]">
            GLOBAL SOURCING <span aria-hidden="true"></span> PARTNER
          </p>
        </div>
      </div>
    </section>;
}